import React, { createContext, useContext, useState, ReactNode } from "react";

interface MetaPixelContextType {
  pixelId: string | null;
  initPixel: (id: string) => void;
  trackEvent: (eventName: string, params?: Record<string, any>) => void;
  trackCustom: (eventName: string, params?: Record<string, any>) => void;
}

const MetaPixelContext = createContext<MetaPixelContextType | undefined>(
  undefined
);

interface MetaPixelProviderProps {
  children: ReactNode;
}

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

export const MetaPixelProvider: React.FC<MetaPixelProviderProps> = ({
  children,
}) => {
  const [pixelId, setPixelId] = useState<string | null>(null);

  // Script loading function
  const loadFbPixelScript = (): void => {
    if (document.getElementById("fb-pixel")) return;

    const script = document.createElement("script");
    script.id = "fb-pixel";
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    `;
    document.head.appendChild(script);
  };

  // Initialize a pixel
  const initPixel = (id: string): void => {
    if (!id) return;

    // Load the script first if not already loaded
    if (!window.fbq) {
      loadFbPixelScript();
    }

    // Set as current pixel and initialize it
    setPixelId(id);
    window.fbq("init", id);
    window.fbq("track", "PageView");
  };

  // Track an event
  const trackEvent = (
    eventName: string,
    params: Record<string, any> = {}
  ): void => {
    if (window.fbq && pixelId) {
      window.fbq("track", eventName, params);
    }
  };

  // Track custom event
  const trackCustom = (
    eventName: string,
    params: Record<string, any> = {}
  ): void => {
    if (window.fbq && pixelId) {
      window.fbq("trackCustom", eventName, params);
    }
  };

  const contextValue: MetaPixelContextType = {
    pixelId,
    initPixel,
    trackEvent,
    trackCustom,
  };

  return (
    <MetaPixelContext.Provider value={contextValue}>
      {children}
    </MetaPixelContext.Provider>
  );
};

export const useMetaPixelContext = (): MetaPixelContextType => {
  const context = useContext(MetaPixelContext);
  if (context === undefined) {
    throw new Error(
      "useMetaPixelContext must be used within a MetaPixelProvider"
    );
  }
  return context;
};
